import { User, UserForResolver } from "db/definitions";
import { Dispatch, SetStateAction, useState } from "react";
import { singletonHook } from "react-singleton-hook";
import { recordSentry } from "utils/recordSentry";
import { Colors } from "@factorearth/component-library";
import { lightTheme, darkTheme } from "@factorearth/component-library";

type SignOutHook = ((data?: any) => void) | undefined;
export type ThemePreference = "INITIAL" | boolean | undefined;

interface SingletonProps {
	mode: ThemePreference;
	handleEndSession: (signOut?: SignOutHook) => Promise<void>;
	handleThemePreferences: (user: User | UserForResolver) => void;
	setMode: Dispatch<SetStateAction<ThemePreference>>;
}

let globalSetMode = (mode: ThemePreference): never | Dispatch<SetStateAction<ThemePreference>> | void => {
	throw new Error("you must useSingleton before setting its state");
};

const init: SingletonProps = {
	mode: true,
	handleEndSession: async (signOut?: SignOutHook) => {},
	handleThemePreferences: () => {},
	setMode: () => {},
};

let currentMode: ThemePreference = init.mode;

const useSingletonImpl = (): SingletonProps => {
	const [mode, setMode] = useState<ThemePreference>(true);

	const handleThemePreferences = (user: User | UserForResolver) => {
		if (user.lightMode === undefined) {
			user.lightMode = true;
		}
		setMode(user.lightMode);
	};

	const handleEndSession = async (signOut?: SignOutHook) => {
		try {
			if (signOut) signOut();
		} catch (error) {
			recordSentry({ error });
		}
	};
	globalSetMode = setMode;
	currentMode = mode;
	return {
		mode,
		handleEndSession,
		handleThemePreferences,
		setMode,
	};
};

export const setDarkMode = (mode: ThemePreference) => globalSetMode(mode);
export const useTheme = (): Colors => {
	return currentMode ? lightTheme : darkTheme;
};

export const useSingleton = singletonHook<SingletonProps>(init, useSingletonImpl);
